import { GAMEPLAY_CONFIG } from '@brick-rogue/game-content';
import type {
  GameplayAnalyticsEvent,
  ObjectiveId,
  SeasonChallengeId,
} from '@brick-rogue/game-core';
import {
  GameSaveRepository,
  createDefaultGameSave,
  createSaveExport,
  migrateGameSave,
  recordBuildChallengeRun,
  recordCareerRun,
  recordDailyChallengeRun,
  type CampaignSaveProgress,
  type BuildChallengeRunResult,
  type CareerRunResult,
  type DailyChallengeRunResult,
  type GamePreferences,
  type GameSave,
  type SaveLoadStatus,
} from '@brick-rogue/persistence';
import { create } from 'zustand';
import { captureException } from '../../monitoring';
import { applyGameplayObjectiveEvent } from '../objectives/objectiveProgress';
import { applySeasonGameplayEvent } from '../seasons/seasonProgress';

export type SaveSystemStatus = 'idle' | 'loading' | 'ready' | 'recovered' | 'degraded';

interface SaveStore {
  readonly status: SaveSystemStatus;
  readonly loadStatus: SaveLoadStatus | null;
  readonly message: string | null;
  readonly save: GameSave;
  readonly recentObjectiveUnlocks: readonly ObjectiveId[];
  readonly recentSeasonUnlocks: readonly SeasonChallengeId[];
  readonly initialize: () => Promise<GameSave>;
  readonly updateCampaign: (campaign: CampaignSaveProgress) => void;
  readonly updatePreferences: (preferences: Partial<GamePreferences>) => void;
  readonly recordRun: (result: CareerRunResult) => void;
  readonly recordDailyChallenge: (result: DailyChallengeRunResult) => void;
  readonly recordBuildChallenge: (result: BuildChallengeRunResult) => void;
  readonly recordGameplayObjectiveEvent: (event: GameplayAnalyticsEvent) => readonly ObjectiveId[];
  readonly recordGameplaySeasonEvent: (
    event: GameplayAnalyticsEvent,
  ) => readonly SeasonChallengeId[];
  readonly exportSave: () => string;
  readonly importSave: (serialized: string) => Promise<GameSave>;
  readonly resetSave: () => Promise<GameSave>;
}

const MAX_LEVELS = GAMEPLAY_CONFIG.run.maxWaves;
const LEGACY_CAMPAIGN_KEY = 'rogue-brick-breaker:campaign:m1';
const LEGACY_PREFERENCES_KEY = 'rogue-brick-breaker:preferences:m6';
const LEGACY_TUTORIAL_KEY = 'rogue-brick-breaker:onboarding:m5';

let repository: GameSaveRepository | null = null;
let initializationPromise: Promise<GameSave> | null = null;
let writeQueue: Promise<void> = Promise.resolve();

const initialSave = createDefaultGameSave(undefined, MAX_LEVELS);

export const useSaveStore = create<SaveStore>((set, get) => {
  const queueSave = (save: GameSave) => {
    if (!repository) {
      return;
    }
    writeQueue = writeQueue
      .then(async () => {
        await repository?.save(save);
      })
      .catch((error: unknown) => {
        captureException(error, 'save-write');
        set({
          status: 'degraded',
          message: `存档暂时仅保存在本次页面：${toErrorMessage(error)}`,
        });
      });
  };

  return {
    status: 'idle',
    loadStatus: null,
    message: null,
    save: initialSave,
    recentObjectiveUnlocks: [],
    recentSeasonUnlocks: [],
    initialize: () => {
      if (initializationPromise) {
        return initializationPromise;
      }
      set({ status: 'loading', message: null });
      initializationPromise = (async () => {
        try {
          repository = new GameSaveRepository(undefined, { maxLevels: MAX_LEVELS });
          const result = await repository.loadOrCreate(readLegacySavePayload());
          if (result.status === 'migrated') {
            clearLegacySavePayload();
          }
          set({
            save: result.save,
            recentObjectiveUnlocks: [],
            recentSeasonUnlocks: [],
            status: result.status === 'recovered' ? 'recovered' : 'ready',
            loadStatus: result.status,
            message: result.message,
          });
          return result.save;
        } catch (error) {
          const fallback = readLegacySavePayload();
          const safeSave = fallback
            ? migrateGameSave(fallback, { maxLevels: MAX_LEVELS }).save
            : createDefaultGameSave(undefined, MAX_LEVELS);
          repository = null;
          set({
            save: safeSave,
            recentObjectiveUnlocks: [],
            recentSeasonUnlocks: [],
            status: 'degraded',
            loadStatus: null,
            message: `浏览器存储不可用，本次将使用临时存档：${toErrorMessage(error)}`,
          });
          return safeSave;
        }
      })();
      return initializationPromise;
    },
    updateCampaign: (campaign) => {
      const save = {
        ...get().save,
        campaign,
        updatedAt: new Date().toISOString(),
      } satisfies GameSave;
      set({ save });
      queueSave(save);
    },
    updatePreferences: (preferences) => {
      const save = {
        ...get().save,
        preferences: { ...get().save.preferences, ...preferences },
        updatedAt: new Date().toISOString(),
      } satisfies GameSave;
      set({ save });
      queueSave(save);
    },
    recordRun: (result) => {
      const save = recordCareerRun(get().save, result);
      set({ save });
      queueSave(save);
    },
    recordDailyChallenge: (result) => {
      const save = recordDailyChallengeRun(get().save, result);
      set({ save });
      queueSave(save);
    },
    recordBuildChallenge: (result) => {
      const save = recordBuildChallengeRun(get().save, result);
      set({ save });
      queueSave(save);
    },
    recordGameplayObjectiveEvent: (event) => {
      const current = get();
      const result = applyGameplayObjectiveEvent(current.save, event);
      const recentObjectiveUnlocks =
        event.type === 'run-started'
          ? []
          : [...new Set([...current.recentObjectiveUnlocks, ...result.completedObjectiveIds])];
      if (
        result.save !== current.save ||
        recentObjectiveUnlocks.length !== current.recentObjectiveUnlocks.length
      ) {
        set({ save: result.save, recentObjectiveUnlocks });
      }
      if (result.save !== current.save) {
        queueSave(result.save);
      }
      return result.completedObjectiveIds;
    },
    recordGameplaySeasonEvent: (event) => {
      const current = get();
      const result = applySeasonGameplayEvent(current.save, event);
      const recentSeasonUnlocks =
        event.type === 'run-started'
          ? []
          : [...new Set([...current.recentSeasonUnlocks, ...result.completedSeasonChallengeIds])];
      if (
        result.save !== current.save ||
        recentSeasonUnlocks.length !== current.recentSeasonUnlocks.length
      ) {
        set({ save: result.save, recentSeasonUnlocks });
      }
      if (result.save !== current.save) {
        queueSave(result.save);
      }
      return result.completedSeasonChallengeIds;
    },
    exportSave: () => createSaveExport(get().save),
    importSave: async (serialized) => {
      if (!repository) {
        throw new Error('当前浏览器存储不可用，无法导入永久存档');
      }
      await writeQueue;
      const save = await repository.import(serialized);
      set({
        save,
        recentObjectiveUnlocks: [],
        recentSeasonUnlocks: [],
        status: 'ready',
        loadStatus: 'loaded',
        message: '存档导入成功',
      });
      return save;
    },
    resetSave: async () => {
      await writeQueue;
      const save = repository
        ? await repository.reset()
        : createDefaultGameSave(undefined, MAX_LEVELS);
      set({
        save,
        recentObjectiveUnlocks: [],
        recentSeasonUnlocks: [],
        status: repository ? 'ready' : 'degraded',
        loadStatus: repository ? 'created' : null,
        message: '存档已重置',
      });
      return save;
    },
  };
});

function readLegacySavePayload(): unknown | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const campaign = parseLegacyValue(window.localStorage.getItem(LEGACY_CAMPAIGN_KEY));
    const preferences = parseLegacyValue(window.localStorage.getItem(LEGACY_PREFERENCES_KEY));
    const tutorialComplete = window.localStorage.getItem(LEGACY_TUTORIAL_KEY) === 'complete';
    if (!campaign && !preferences && !tutorialComplete) {
      return undefined;
    }
    return {
      schemaVersion: 1,
      ...(campaign ? { campaign } : {}),
      preferences: {
        ...(preferences ?? {}),
        tutorialComplete,
      },
    };
  } catch {
    return undefined;
  }
}

function clearLegacySavePayload(): void {
  try {
    window.localStorage.removeItem(LEGACY_CAMPAIGN_KEY);
    window.localStorage.removeItem(LEGACY_PREFERENCES_KEY);
    window.localStorage.removeItem(LEGACY_TUTORIAL_KEY);
  } catch {
    // The validated Dexie save remains authoritative when legacy cleanup is unavailable.
  }
}

function parseLegacyValue(serialized: string | null): Record<string, unknown> | null {
  if (!serialized) {
    return null;
  }
  const parsed = JSON.parse(serialized) as unknown;
  return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '未知错误';
}
